import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/core/combobox';

import type { AriaProps, Option } from './types';
import { optionLabel } from './value-helpers';

type MultiSelectComboboxProps = AriaProps & {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  /** When set, input changes drive a server search (entity-select); omit for fixed/preloaded lists. */
  onSearch?: (search: string) => void;
  emptyText: string;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  className?: string;
};

/**
 * Multi-value combobox rendering the selection as removable chips, following the
 * `tags-input` shape. Backs both the fixed `multi-select` and the multi
 * `entity-select` (which additionally drives `onSearch` in autocomplete mode).
 */
export function MultiSelectCombobox({
  options,
  value,
  onChange,
  onSearch,
  emptyText,
  onBlur,
  disabled,
  placeholder,
  id,
  className,
  ...aria
}: MultiSelectComboboxProps) {
  const anchorRef = useComboboxAnchor();

  return (
    <Combobox
      items={options}
      // Server-search results are already filtered; fixed/preloaded lists omit
      // this prop so base-ui filters locally.
      filteredItems={onSearch ? options : undefined}
      multiple
      value={value}
      disabled={disabled}
      itemToStringLabel={(item) => optionLabel(options, item)}
      // Base UI clears the whole selection on Escape with the popup closed;
      // never treat that as a real change (mirrors `TagsInput`).
      onValueChange={(next, eventDetails) => {
        if (eventDetails.reason === 'escape-key') {
          return;
        }
        onChange(Array.isArray(next) ? next : []);
      }}
      onInputValueChange={
        onSearch
          ? (inputValue, eventDetails) => {
              if (eventDetails.reason === 'item-press') {
                return;
              }
              if (typeof inputValue === 'string') {
                onSearch(inputValue);
              }
            }
          : undefined
      }
    >
      <ComboboxChips ref={anchorRef} className={className}>
        <ComboboxValue>
          {(selected: string[]) => (
            <>
              {selected.map((selectedValue) => (
                <ComboboxChip key={selectedValue} showRemove={!disabled}>
                  {optionLabel(options, selectedValue)}
                </ComboboxChip>
              ))}
              <ComboboxChipsInput
                {...aria}
                id={id}
                disabled={disabled}
                placeholder={selected.length === 0 ? placeholder : undefined}
                onBlur={onBlur}
              />
            </>
          )}
        </ComboboxValue>
      </ComboboxChips>
      <ComboboxContent anchor={anchorRef}>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(option: Option) => (
            <ComboboxItem key={option.value} value={option.value}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
